import { useEffect } from 'react';

const DEFAULTS = {
  title: 'WaapLeads CRM | AI CRM, WhatsApp Automation & Meta Ads Platform',
  description:
    'WaapLeads CRM helps teams manage AI-powered CRM workflows, WhatsApp Business automation, Meta Ads campaigns, lead pipelines, and customer engagement from one platform.',
  keywords: [
    'AI CRM',
    'WhatsApp CRM',
    'WhatsApp Business automation',
    'Meta Ads management',
    'marketing automation',
    'lead management software',
    'customer engagement platform',
    'sales CRM India',
    'WaapLeads CRM',
  ],
  type: 'website',
  pathname: '/',
  image: '/branding/waapleads-logo.svg?v=2',
};

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

export default function Seo({
  title = DEFAULTS.title,
  description = DEFAULTS.description,
  keywords = DEFAULTS.keywords,
  pathname = DEFAULTS.pathname,
  type = DEFAULTS.type,
  image = DEFAULTS.image,
  structuredData,
}) {
  useEffect(() => {
    const origin = window.location.origin;
    const canonicalUrl = new URL(pathname, origin).toString();
    const imageUrl = new URL(image, origin).toString();
    const keywordText = Array.isArray(keywords) ? keywords.join(', ') : keywords;

    document.title = title;

    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywordText });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow, max-image-preview:large' });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'WaapLeads CRM' });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    let script = document.head.querySelector('#waapleads-structured-data');
    if (!script) {
      script = document.createElement('script');
      script.id = 'waapleads-structured-data';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    const schemaPayload = structuredData || {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'WaapLeads CRM',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description,
      url: canonicalUrl,
      image: imageUrl,
      brand: {
        '@type': 'Brand',
        name: 'WaapLeads CRM',
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
      },
    };

    script.textContent = JSON.stringify(schemaPayload);
  }, [description, image, keywords, pathname, structuredData, title, type]);

  return null;
}
